import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import CheckToggleField from 'components/CheckToggleField';
import TextAreaField from 'components/TextAreaField';
import useCallbackAsync from 'hooks/useCallbackAsync';
import SelectLeads from '../SelectLeads';
import api from 'api';
import { BuyerEnquiryHistory, LeadStagesCopies } from 'models/DealRoom';
import { getHasElementsInArray, getIsArrayEmpty } from 'utils';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { DEFAULT_LIMIT } from 'data/global';
import useLeadStagesCopy from 'hooks/useLeadStagesCopy';
import { LEAD_STAGES } from 'models/Lead';
import ResolveMultiSelect from 'components/ResolveMultiSelect';
import SelectRowsPlaceholder from 'components/SelectRowsPlaceholder';
import useDebounceSearch from 'hooks/useDebounceSearch';
import { getQueryArrayParam, SORT_VALUES } from '../../utils';
import useMatchMedia from 'hooks/useMatchMedia';

import './InviteLeadsForm.styles.scss';

// Widths of the "previous enquiries" and "last enquiry" columns in SelectLeads.
const TRAILING_COLUMN_WIDTHS = [204, 100];

export type InviteLeadsFormValues = {
  leads: Record<string, boolean>;
  includeMessage?: boolean;
  customMessage?: string;
};

type InviteLeadsFormProps = {
  className?: string;
  dealRoomId: string;
  leadStagesCopies?: LeadStagesCopies;
  initialValues?: Partial<InviteLeadsFormValues>;
  submitText: string;
  onSubmit: (values: InviteLeadsFormValues) => void;
  onClose: () => void;
};

const InviteLeadsForm: React.FC<InviteLeadsFormProps> = (props) => {
  const {
    className,
    dealRoomId,
    leadStagesCopies,
    initialValues,
    submitText,
    onSubmit,
    onClose,
  } = props;

  const [sort, setSort] = useState(`-${SORT_VALUES.NUMBER_OF_ENQUIRIES}`);
  const [dealRoomOptions, setDealRoomOptions] = useState([]);
  const [dealRoomIds, setDealRoomIds] = useState<string[]>([]);
  const [buyerSteps, setBuyerSteps] = useState<string[]>([]);

  const classes = classNames('invite-leads-form', className);

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const copies = useLeadStagesCopy().getCopies(leadStagesCopies);

  const leadStageOptions = useMemo(
    () =>
      Object.values(LEAD_STAGES).map((value, index) => ({
        value,
        label: `${copies[value]} (${t('stage')} ${index + 1})`,
      })),
    [copies, t],
  );

  const [onSearch, isSearchingDeals] = useCallbackAsync(
    async (searchString: string) => {
      if (!searchString) {
        setDealRoomOptions([]);
        return;
      }

      const { data } = await api.portfolio.seller.searchDeals(
        dealRoomId,
        searchString,
      );

      setDealRoomOptions(
        data.map(({ _id, name }) => ({ value: _id, label: name })),
      );
    },
  );

  const { isDebouncing, onSearchInputChange } = useDebounceSearch({ onSearch });

  const isSearchingDealsWithDebounce = isSearchingDeals || isDebouncing;

  const { loading, items, totalItems, onContainerScrolled } =
    useInfinitePagination<BuyerEnquiryHistory>({
      uniqueKey: 'buyerEmail',
      condition: !!dealRoomId,
      resetItemsWhenReset: false,
      resetDeps: [dealRoomId, dealRoomIds, buyerSteps, sort],
      dependencies: [dealRoomId, dealRoomIds, buyerSteps, sort],
      makeRequest: async (currentPage) =>
        api.dealRoom
          .getPreviousDealsLeads(dealRoomId, {
            $page: currentPage.toString(),
            $limit: DEFAULT_LIMIT,
            $sort: sort,
            dealRoomIds: getQueryArrayParam(dealRoomIds),
            buyerSteps: getQueryArrayParam(buyerSteps),
          })
          .then(({ data }) => data),
    });

  const itemsHasElements = getHasElementsInArray(items);

  const isInitialLoading = loading && !itemsHasElements;

  const isEmpty =
    !loading &&
    !itemsHasElements &&
    getIsArrayEmpty(dealRoomIds) &&
    getIsArrayEmpty(buyerSteps);

  if (isEmpty) return <p className="empty-leads">{t('noLeadsFromDeal')}</p>;

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({
        submitting,
        invalid,
        values: { includeMessage },
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {/* The initial fetch is covered by the leads placeholder, so the
              modal keeps its size instead of growing once leads arrive. */}
          {((loading && !isInitialLoading) || submitting) && <Loader />}

          <div className="invite-leads-form__filter">
            <p>{t('filterLeadsByDealsTheyEnquired')}</p>
            <ResolveMultiSelect
              value={dealRoomIds}
              options={dealRoomOptions}
              hasCheckbox
              renderAsPortal={!isPhablet}
              loading={isSearchingDealsWithDebounce}
              noDataTitle={t('typeToViewAvailableOptions')}
              placeholder={t('selectDeal')}
              emptySearchTitle={t('foundNoDeals')}
              onSearchChange={onSearchInputChange}
              onChange={setDealRoomIds}
            />
          </div>

          <div className="invite-leads-form__filter">
            <p>{t('filterLeadsByTheirLatestStage')}</p>
            <ResolveMultiSelect
              value={buyerSteps}
              searchable={false}
              hasCheckbox
              renderAsPortal={!isPhablet}
              placeholder={t('selectStage')}
              options={leadStageOptions}
              onChange={setBuyerSteps}
            />
          </div>

          {isInitialLoading ? (
            <SelectRowsPlaceholder
              hasCount
              trailingWidths={TRAILING_COLUMN_WIDTHS}
            />
          ) : itemsHasElements ? (
            <>
              <p className="displayed-leads">
                {t('displayedLeads', { count: totalItems })}
              </p>
              <SelectLeads
                leads={items}
                sort={sort}
                updateSort={setSort}
                onContainerScroll={onContainerScrolled}
              />
            </>
          ) : (
            <p className="empty-leads">{t('noLeadsFromDeal')}</p>
          )}

          {(isInitialLoading || itemsHasElements) && (
            <>
              <Field
                name="includeMessage"
                type="checkbox"
                label={t('includeMessage')}
                component={CheckToggleField}
              />
              {includeMessage && (
                <Field
                  name="customMessage"
                  component={TextAreaField}
                  rows={3}
                />
              )}
            </>
          )}

          <div className="actions">
            <Button variant="link" onClick={onClose}>
              {t('cancel')}
            </Button>

            <Button
              type="submit"
              disabled={submitting || invalid || !itemsHasElements}
            >
              {submitText}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default InviteLeadsForm;
